import { DatePickerField, InputField, RadioListField } from 'lib/iterable-hook-form/FormField';
import noop from 'lodash/noop';
import { OptionText } from 'modules/campaigns/editor/styles';
import { EndRecurrenceType, ScheduleEligibilityFormState } from 'modules/campaigns/editor/types';
import { useEffect, useMemo } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { match } from 'ts-pattern';
import { ErrorText, FormField } from 'ui/formField';
import { RadioListVariantProps } from 'ui/radioList';
import { spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { SCHEDULE_CAMPAIGN_FORM_FIELDS } from '../ScheduleCampaignModal.constants';
import { RowWrapper } from '../ScheduleCampaignModal.styled';
import {
  getCampaignRecurringEndOnDateRange,
  getScheduleCampaignTargetTimeZone,
} from '../ScheduleCampaignModal.utils';

export const EndRecurrenceTypeLabel = styled(OptionText)`
  width: ${pxToRem(46)};
`;

export const EndSendsCountInputField = styled(InputField)`
  width: ${pxToRem(145)};
  input {
    width: ${spacing.base};
  }
`;

export const EndSendsDatePickerField = styled(DatePickerField)`
  form {
    width: ${pxToRem(145)};
  }
`;

export const EndSendsRadioListField = styled(RadioListField)`
  width: 100%;
`;

const StyledErrorText = styled(ErrorText)`
  margin: 0 0 0 ${spacing.double};
`;

const EndRecurrenceOnDate = () => {
  const { errors } = useFormState<ScheduleEligibilityFormState>({
    name: ['endDate'],
  });

  const [projectTimeZone, startingTimeZone, scheduleType] = useWatch({
    name: ['projectTimeZone', 'startingTimeZone', 'scheduleType'],
  });

  const validDateRange = useMemo(() => {
    const targetTz = getScheduleCampaignTargetTimeZone(
      projectTimeZone,
      startingTimeZone,
      scheduleType,
    );

    return getCampaignRecurringEndOnDateRange(targetTz);
  }, [projectTimeZone, startingTimeZone, scheduleType]);

  const endDateError = errors?.endDate;

  return (
    <>
      <RowWrapper>
        <EndRecurrenceTypeLabel>On</EndRecurrenceTypeLabel>
        <EndSendsDatePickerField
          name="endDate"
          validDateRange={validDateRange}
          automaticallyAvoidCollisions
        />
      </RowWrapper>
      {endDateError?.message && <StyledErrorText>{endDateError.message}</StyledErrorText>}
    </>
  );
};

const END_RECURRENCE_OPTIONS: RadioListVariantProps['options'] = [
  {
    id: EndRecurrenceType.Never,
    value: EndRecurrenceType.Never,
    name: () => <OptionText>Never</OptionText>,
  },
  {
    id: EndRecurrenceType.OnDate,
    value: EndRecurrenceType.OnDate,
    name: EndRecurrenceOnDate,
  },
];

export const EndRecurrenceScheduleTypes = () => {
  const { watch, trigger } = useFormContext();

  useEffect(() => {
    watch((_, { name }) => {
      match(name)
        .with('endType', () => trigger('endDate'))
        .otherwise(noop);
    });
  }, [watch, trigger]);

  return (
    <FormField labelText="End">
      <EndSendsRadioListField
        name={SCHEDULE_CAMPAIGN_FORM_FIELDS.END_TYPE}
        options={END_RECURRENCE_OPTIONS}
      />
    </FormField>
  );
};
